import { computed, inject } from '@angular/core';
import { ApiService } from '@libs/core/base-config';
import {
  AsyncState,
  asResult,
  computedFailure,
  dataFromState,
  initialState,
  loadingState,
  successState,
} from '@libs/core/lib/state/async-states';
import {
  adminVendorsDecision,
  adminVendorsList,
  getVendorById,
  requestToPublishVendor,
  unPublishVendor,
  vendorCreate,
  vendorDelete,
  vendorUpdate,
  vendorsList,
  vendorsListByFilter,
  vendorsListForVendors,
} from '@libs/mtjr/shared/generated/entities/vendor/service/vendor.service';
import { Dtos, Entity } from '@libs/mtjr/shared/generated/types';
import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';

export function withVendorsList() {
  type VendorsListState = {
    vendorsListState: AsyncState<Dtos.VendorsListResponse>;
    vendorsListInput: Dtos.VendorsListQueryParams;
    vendorsListCanFetchNext: boolean;
    vendorsListFetchingNext: boolean;
  };
  return signalStoreFeature(
    withState<VendorsListState>({
      vendorsListState: initialState(),
      vendorsListInput: { page: 1, totalPerPage: 20 },
      vendorsListCanFetchNext: false,
      vendorsListFetchingNext: false,
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async vendorsListPage(input?: Partial<Dtos.VendorsListQueryParams>) {
        const mergedInput = { ...store.vendorsListInput(), ...input };
        patchState(store, {
          vendorsListState: mergedInput.page === 1 ? loadingState() : store.vendorsListState(),
          vendorsListInput: mergedInput,
          vendorsListFetchingNext: mergedInput.page > 1,
        });
        const result = await asResult(vendorsList(apiService, store.vendorsListInput()));
        if (result.success) {
          const cState = store.vendorsListState();
          let vendors = result.data.vendors;
          if (cState.success && mergedInput.page > 1) {
            vendors = [...cState.data.vendors, ...vendors];
          }
          patchState(store, {
            vendorsListState: successState({ ...result.data, vendors: vendors }),
            vendorsListCanFetchNext: result.data.vendors.length === mergedInput.totalPerPage,
            vendorsListFetchingNext: false,
          });
        } else {
          patchState(store, {
            vendorsListState: result,
            vendorsListCanFetchNext: false,
            vendorsListFetchingNext: false,
          });
        }
      },

      async vendorsListFetchNextPage() {
        if (store.vendorsListCanFetchNext() === false) return;
        const input = store.vendorsListInput();
        await this.vendorsListPage({ ...input, page: input.page + 1 });
      },
    })),

    withComputed((store) => ({
      vendors: dataFromState(store.vendorsListState, (data) => data?.vendors ?? []),
      vendorsFailure: computedFailure(store.vendorsListState),
    })),
  );
}

export function withVendorsListForVendors() {
  type VendorsListForVendorsState = {
    vendorsListForVendorsState: AsyncState<Dtos.VendorsListForVendorsResponse>;
    vendorsListForVendorsInput: Dtos.VendorsListForVendorsQueryParams;
    vendorsListForVendorsCanFetchNext: boolean;
    vendorsListForVendorsFetchingNext: boolean;
  };
  return signalStoreFeature(
    withState<VendorsListForVendorsState>({
      vendorsListForVendorsState: initialState(),
      vendorsListForVendorsInput: { page: 1, totalPerPage: 20 },
      vendorsListForVendorsCanFetchNext: false,
      vendorsListForVendorsFetchingNext: false,
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async vendorsListForVendorsPage(input?: Partial<Dtos.VendorsListForVendorsQueryParams>) {
        const mergedInput = { ...store.vendorsListForVendorsInput(), ...input };
        patchState(store, {
          vendorsListForVendorsState: mergedInput.page === 1 ? loadingState() : store.vendorsListForVendorsState(),
          vendorsListForVendorsInput: mergedInput,
          vendorsListForVendorsFetchingNext: mergedInput.page > 1,
        });
        const result = await asResult(vendorsListForVendors(apiService, store.vendorsListForVendorsInput()));
        if (result.success) {
          const cState = store.vendorsListForVendorsState();
          let vendors = result.data.vendors;
          if (cState.success && mergedInput.page > 1) {
            vendors = [...cState.data.vendors, ...vendors];
          }
          patchState(store, {
            vendorsListForVendorsState: successState({ ...result.data, vendors: vendors }),
            vendorsListForVendorsCanFetchNext: result.data.vendors.length === mergedInput.totalPerPage,
            vendorsListForVendorsFetchingNext: false,
          });
        } else {
          patchState(store, {
            vendorsListForVendorsState: result,
            vendorsListForVendorsCanFetchNext: false,
            vendorsListForVendorsFetchingNext: false,
          });
        }
      },

      async vendorsListForVendorsFetchNextPage() {
        if (store.vendorsListForVendorsCanFetchNext() === false) return;
        const input = store.vendorsListForVendorsInput();
        await this.vendorsListForVendorsPage({ ...input, page: input.page + 1 });
      },
    })),

    withComputed((store) => ({
      vendors: dataFromState(store.vendorsListForVendorsState, (data) => data?.vendors ?? []),
      vendorsFailure: computedFailure(store.vendorsListForVendorsState),
    })),
  );
}

export function withAdminVendorsList() {
  type AdminVendorsListState = {
    adminVendorsListState: AsyncState<Dtos.AdminVendorsListResponse>;
    adminVendorsListInput: Dtos.AdminVendorsListQueryParams;
    adminVendorsListCanFetchNext: boolean;
    adminVendorsListFetchingNext: boolean;
  };
  return signalStoreFeature(
    withState<AdminVendorsListState>({
      adminVendorsListState: initialState(),
      adminVendorsListInput: { page: 1, totalPerPage: 20 },
      adminVendorsListCanFetchNext: false,
      adminVendorsListFetchingNext: false,
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async adminVendorsListPage(input?: Partial<Dtos.AdminVendorsListQueryParams>) {
        const mergedInput = { ...store.adminVendorsListInput(), ...input };
        patchState(store, {
          adminVendorsListState: mergedInput.page === 1 ? loadingState() : store.adminVendorsListState(),
          adminVendorsListInput: mergedInput,
          adminVendorsListFetchingNext: mergedInput.page > 1,
        });
        const result = await asResult(adminVendorsList(apiService, store.adminVendorsListInput()));
        if (result.success) {
          const cState = store.adminVendorsListState();
          let vendors = result.data.vendors;
          if (cState.success && mergedInput.page > 1) {
            vendors = [...cState.data.vendors, ...vendors];
          }
          patchState(store, {
            adminVendorsListState: successState({ ...result.data, vendors: vendors }),
            adminVendorsListCanFetchNext: result.data.vendors.length === mergedInput.totalPerPage,
            adminVendorsListFetchingNext: false,
          });
        } else {
          patchState(store, {
            adminVendorsListState: result,
            adminVendorsListCanFetchNext: false,
            adminVendorsListFetchingNext: false,
          });
        }
      },

      async adminVendorsListFetchNextPage() {
        if (store.adminVendorsListCanFetchNext() === false) return;
        const input = store.adminVendorsListInput();
        await this.adminVendorsListPage({ ...input, page: input.page + 1 });
      },
    })),

    withComputed((store) => ({
      vendors: dataFromState(store.adminVendorsListState, (data) => data?.vendors ?? []),
      vendorsFailure: computedFailure(store.adminVendorsListState),
    })),
  );
}

export function withVendorsListByFilter() {
  type VendorsListByFilterState = {
    vendorsListByFilterState: AsyncState<Dtos.VendorsListByFilterResponse>;
    vendorsListByFilterInput: Dtos.VendorsListByFilterQueryParams;
    vendorsListByFilterCanFetchNext: boolean;
    vendorsListByFilterFetchingNext: boolean;
  };
  return signalStoreFeature(
    withState<VendorsListByFilterState>({
      vendorsListByFilterState: initialState(),
      vendorsListByFilterInput: { page: 1, totalPerPage: 20 },
      vendorsListByFilterCanFetchNext: false,
      vendorsListByFilterFetchingNext: false,
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async vendorsListByFilterPage(input?: Partial<Dtos.VendorsListByFilterQueryParams>) {
        const mergedInput = { ...store.vendorsListByFilterInput(), ...input };
        patchState(store, {
          vendorsListByFilterState: mergedInput.page === 1 ? loadingState() : store.vendorsListByFilterState(),
          vendorsListByFilterInput: mergedInput,
          vendorsListByFilterFetchingNext: mergedInput.page > 1,
        });
        const result = await asResult(vendorsListByFilter(apiService, store.vendorsListByFilterInput()));
        if (result.success) {
          const cState = store.vendorsListByFilterState();
          let vendors = result.data.vendors;
          if (cState.success && mergedInput.page > 1) {
            vendors = [...cState.data.vendors, ...vendors];
          }
          patchState(store, {
            vendorsListByFilterState: successState({ ...result.data, vendors: vendors }),
            vendorsListByFilterCanFetchNext: result.data.vendors.length === mergedInput.totalPerPage,
            vendorsListByFilterFetchingNext: false,
          });
        } else {
          patchState(store, {
            vendorsListByFilterState: result,
            vendorsListByFilterCanFetchNext: false,
            vendorsListByFilterFetchingNext: false,
          });
        }
      },

      async vendorsListByFilterFetchNextPage() {
        if (store.vendorsListByFilterCanFetchNext() === false) return;
        const input = store.vendorsListByFilterInput();
        await this.vendorsListByFilterPage({ ...input, page: input.page + 1 });
      },
    })),

    withComputed((store) => ({
      vendors: dataFromState(store.vendorsListByFilterState, (data) => data?.vendors ?? []),
      vendorsFailure: computedFailure(store.vendorsListByFilterState),
    })),
  );
}

export function withRequestToPublishVendor() {
  return signalStoreFeature(
    withState<{ requestToPublishVendorState: AsyncState<void> }>({
      requestToPublishVendorState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async requestToPublishVendor({ vendorId }: Dtos.RequestToPublishVendorPathParams) {
        patchState(store, { requestToPublishVendorState: loadingState() });
        const result = await asResult(requestToPublishVendor(apiService, { vendorId }));
        patchState(store, { requestToPublishVendorState: result });
      },
    })),
    withComputed((store) => ({
      requestToPublishVendorLoading: computed(() => store.requestToPublishVendorState().type === 'loading'),
      requestToPublishVendorFailure: computedFailure(store.requestToPublishVendorState),
    })),
  );
}

export function withUnPublishVendor() {
  return signalStoreFeature(
    withState<{ unPublishVendorState: AsyncState<void> }>({
      unPublishVendorState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async unPublishVendor({ vendorId }: Dtos.UnPublishVendorPathParams) {
        patchState(store, { unPublishVendorState: loadingState() });
        const result = await asResult(unPublishVendor(apiService, { vendorId }));
        patchState(store, { unPublishVendorState: result });
      },
    })),
    withComputed((store) => ({
      unPublishVendorLoading: computed(() => store.unPublishVendorState().type === 'loading'),
      unPublishVendorFailure: computedFailure(store.unPublishVendorState),
    })),
  );
}

export function withAdminVendorsDecision() {
  return signalStoreFeature(
    withState<{ adminVendorsDecisionState: AsyncState<void> }>({
      adminVendorsDecisionState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async adminVendorsDecision(adminVendorsDecisionList: Dtos.AdminVendorsDecisionList) {
        patchState(store, { adminVendorsDecisionState: loadingState() });
        const result = await asResult(adminVendorsDecision(apiService, adminVendorsDecisionList));
        patchState(store, { adminVendorsDecisionState: result });
      },
    })),
    withComputed((store) => ({
      adminVendorsDecisionLoading: computed(() => store.adminVendorsDecisionState().type === 'loading'),
      adminVendorsDecisionFailure: computedFailure(store.adminVendorsDecisionState),
    })),
  );
}

export function withVendorCreate() {
  return signalStoreFeature(
    withState<{ vendorCreateState: AsyncState<Entity.Vendor> }>({
      vendorCreateState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async vendorCreate(vendorCreateBody: Dtos.VendorCreateBody) {
        patchState(store, { vendorCreateState: loadingState() });
        const result = await asResult(vendorCreate(apiService, vendorCreateBody));
        patchState(store, { vendorCreateState: result });
      },
    })),
    withComputed((store) => ({
      vendorCreateLoading: computed(() => store.vendorCreateState().type === 'loading'),
      vendorCreateFailure: computedFailure(store.vendorCreateState),
    })),
  );
}

export function withVendorDelete() {
  return signalStoreFeature(
    withState<{ vendorDeleteState: AsyncState<void> }>({
      vendorDeleteState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async vendorDelete({ vendorId }: Dtos.VendorDeletePathParams) {
        patchState(store, { vendorDeleteState: loadingState() });
        const result = await asResult(vendorDelete(apiService, { vendorId }));
        patchState(store, { vendorDeleteState: result });
      },
    })),
    withComputed((store) => ({
      vendorDeleteLoading: computed(() => store.vendorDeleteState().type === 'loading'),
      vendorDeleteFailure: computedFailure(store.vendorDeleteState),
    })),
  );
}

export function withVendorUpdate() {
  return signalStoreFeature(
    withState<{ vendorUpdateState: AsyncState<void> }>({
      vendorUpdateState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async vendorUpdate(vendorUpdateBody: Dtos.VendorUpdateBody, { vendorId }: Dtos.VendorUpdatePathParams) {
        patchState(store, { vendorUpdateState: loadingState() });
        const result = await asResult(vendorUpdate(apiService, vendorUpdateBody, { vendorId }));
        patchState(store, { vendorUpdateState: result });
      },
    })),
    withComputed((store) => ({
      vendorUpdateLoading: computed(() => store.vendorUpdateState().type === 'loading'),
      vendorUpdateFailure: computedFailure(store.vendorUpdateState),
    })),
  );
}

export function withGetVendorById() {
  return signalStoreFeature(
    withState<{ getVendorByIdState: AsyncState<Dtos.Vendor> }>({
      getVendorByIdState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async getVendorById(queryParams: Dtos.GetVendorByIdQueryParams, { vendorId }: Dtos.GetVendorByIdPathParams) {
        patchState(store, { getVendorByIdState: loadingState() });
        const result = await asResult(getVendorById(apiService, queryParams, { vendorId }));
        patchState(store, { getVendorByIdState: result });
      },
    })),
    withComputed((store) => ({
      getVendorByIdLoading: computed(() => store.getVendorByIdState().type === 'loading'),
      getVendorByIdFailure: computedFailure(store.getVendorByIdState),
    })),
  );
}
